import { runPythonCode, initPythonRuntime } from '../services/pythonRuntime';

const JS_KEYWORDS = /(console\.|function\s|=>|\bconst\b|\blet\b|\bvar\b|document\.|window\.)/i;
const PY_KEYWORDS = /(def\s|import\s|print\s*\(|#)/i;

const detectLanguage = (code) => {
  if (!code) return 'python';
  if (JS_KEYWORDS.test(code)) return 'javascript';
  if (PY_KEYWORDS.test(code)) return 'python';
  return 'python';
};

// Simple Python expression evaluator for print statements
const evaluatePythonExpression = (expr, context = {}) => {
  expr = expr.trim();
  
  // Handle string literals
  if ((expr.startsWith('"') && expr.endsWith('"')) || 
      (expr.startsWith("'") && expr.endsWith("'"))) {
    return expr.substring(1, expr.length - 1).replace(/\\n/g, '\n').replace(/\\t/g, '\t');
  }
  
  // Handle concatenation with + (e.g., "Question" + str(i+1) or "Question", i+1)
  // For comma-separated values in print, we'll simulate them
  if (expr.includes(',')) {
    const parts = expr.split(',').map(p => p.trim());
    return parts.map(part => evaluatePythonExpression(part, context)).join(' ');
  }
  
  // Handle string concatenation
  if (expr.includes('+')) {
    const parts = expr.split('+').map(p => p.trim());
    return parts.map(part => evaluatePythonExpression(part, context)).join('');
  }
  
  // Handle variables and simple expressions
  // Check if it's a variable in context
  if (context[expr] !== undefined) {
    return String(context[expr]);
  }
  
  // Handle range() and loop variables (i, j, k, etc.)
  if (/^[ijk]\s*[+\-]\s*\d+$/.test(expr) || /^[ijk]$/.test(expr)) {
    // This will be handled by the loop simulation
    return expr;
  }
  
  // Handle numeric expressions
  try {
    // Replace common Python expressions with JavaScript equivalents
    let jsExpr = expr
      .replace(/\b(\w+)\s*\+\s*(\d+)\b/g, (match, varName, num) => {
        if (context[varName] !== undefined) {
          return String(context[varName] + parseInt(num));
        }
        return match;
      })
      .replace(/\b(\w+)\s*-\s*(\d+)\b/g, (match, varName, num) => {
        if (context[varName] !== undefined) {
          return String(context[varName] - parseInt(num));
        }
        return match;
      });
    
    // Try to evaluate as JavaScript (for simple math)
    if (/^[\d\s+\-*/().]+$/.test(jsExpr)) {
      // eslint-disable-next-line no-eval
      return String(eval(jsExpr));
    }
  } catch (e) {
    // If evaluation fails, return the expression as-is
  }
  
  return expr;
};

const runPythonSimulation = (code) => {
  const lines = code.split(/\r?\n/);
  const outputs = [];
  const context = {}; // Variable context for loops
  
  // Match print() statements
  const printRegex = /print\s*\((.*?)\)/g;
  const print2Regex = /print\s+(.+)/g;
  
  lines.forEach((line, lineIndex) => {
    // Detect for loops: for i in range(n):
    const forLoopMatch = line.match(/for\s+(\w+)\s+in\s+range\s*\((\d+)\)\s*:/);
    if (forLoopMatch) {
      const varName = forLoopMatch[1];
      const rangeEnd = parseInt(forLoopMatch[2]);
      
      // Find the body of the loop (next line or indented lines)
      const loopBody = [];
      let bodyStart = lineIndex + 1;
      
      // Collect indented lines as loop body
      while (bodyStart < lines.length) {
        const nextLine = lines[bodyStart];
        if (nextLine.trim() === '') {
          bodyStart++;
          continue;
        }
        // Check if line is indented (starts with spaces/tabs)
        if (/^\s+/.test(nextLine)) {
          loopBody.push(nextLine);
          bodyStart++;
        } else {
          break;
        }
      }
      
      // Execute loop body for each iteration
      for (let i = 0; i < rangeEnd; i++) {
        context[varName] = i;
        
        // Process each line in loop body
        loopBody.forEach(loopLine => {
          // Check for print statements in loop body
          let match;
          const printRegexLoop = /print\s*\((.*?)\)/g;
          
          while ((match = printRegexLoop.exec(loopLine)) !== null) {
            const printExpr = match[1];
            const evaluated = evaluatePythonExpression(printExpr, context);
            outputs.push(evaluated);
          }
        });
      }
      
      return; // Skip processing this line as a regular print
    }
    
    // Regular print statements (outside loops)
    let match;
    while ((match = printRegex.exec(line)) !== null) {
      const printExpr = match[1];
      const evaluated = evaluatePythonExpression(printExpr, context);
      outputs.push(evaluated);
    }
    
    // Python 2 style: print "hello"
    while ((match = print2Regex.exec(line)) !== null) {
      const printExpr = match[1];
      const evaluated = evaluatePythonExpression(printExpr, context);
      outputs.push(evaluated);
    }
  });

  if (outputs.length === 0) {
    return 'Code executed (no visible output).\n\nNote: This Python simulator captures print() statements and basic loops. For full execution, use a Python IDE.';
  }
  return outputs.join('\n');
};

const normalizeArg = (arg) => {
  if (typeof arg === 'string') return arg;
  if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
  if (arg === undefined || arg === null) return String(arg);
  try {
    return JSON.stringify(arg);
  } catch (err) {
    return '[unserializable output]';
  }
};

const runJavascript = (code) => {
  const logs = [];
  const capture = (...args) => {
    logs.push(args.map(normalizeArg).join(' '));
  };

  const customConsole = {
    log: capture,
    warn: capture,
    error: capture
  };

  // eslint-disable-next-line no-new-func
  const executor = new Function('console', 'print', '"use strict";\n' + code);
  const result = executor(customConsole, (...args) => capture(...args));
  if (result !== undefined) {
    capture(result);
  }
  return logs.length ? logs.join('\n') : 'Code executed (no output).';
};

// Map language names to normalized values
const normalizeLanguage = (lang) => {
  if (!lang) return null;
  const normalized = lang.toLowerCase().trim();
  
  // JavaScript variations
  if (normalized === 'javascript' || normalized === 'js' || normalized === 'jsx') {
    return 'javascript';
  }
  
  // Python variations
  if (normalized === 'python' || normalized === 'py') {
    return 'python';
  }
  
  // Other languages (not executable in browser)
  return normalized;
};

export const runPlaygroundCode = async (code, preferredLanguage = null) => {
  const snippet = (code || '').trim();
  if (!snippet) {
    return {
      language: 'python',
      output: 'No code to run.'
    };
  }

  // Normalize the preferred language
  const normalizedLang = preferredLanguage ? normalizeLanguage(preferredLanguage) : null;
  const language = normalizedLang || detectLanguage(snippet);
  
  try {
    if (language === 'javascript') {
      return {
        language: 'javascript',
        output: runJavascript(snippet)
      };
    }
    
    if (language === 'python') {
      // Use real Python runtime (Pyodide)
      try {
        const result = await runPythonCode(snippet, 10000);
        
        if (result.error) {
          // If there's output before the error, show both
          if (result.output) {
            return {
              language: 'python',
              output: result.output + '\n\n--- Error ---\n' + result.error
            };
          }
          // If only error, show it
          return {
            language: 'python',
            output: `Error: ${result.error}`
          };
        }
        
        return {
          language: 'python',
          output: result.output || 'Code executed (no output).'
        };
      } catch (error) {
        // If Pyodide fails to load or execute, fallback to simulation
        console.warn('Pyodide execution failed, using simulation:', error);
        const simulatedOutput = runPythonSimulation(snippet);
        return {
          language: 'python',
          output: simulatedOutput + '\n\n(Note: Using simulation. Real Python runtime may still be loading. Try again in a moment.)'
        };
      }
    }
    
    // For other languages (Java, C++, SQL, HTML, CSS, etc.), show appropriate message
    const languageName = preferredLanguage || language;
    return {
      language: languageName,
      output: `⚠️ Code execution is not available for ${languageName} in the browser.\n\n` +
              `This code runner currently supports:\n` +
              `• JavaScript (full execution)\n` +
              `• Python (real execution with Pyodide)\n\n` +
              `For ${languageName} code, please use an appropriate IDE or compiler.`
    };
  } catch (error) {
    return {
      language: language || 'unknown',
      output: 'Error: ' + error.message
    };
  }
};
